import { graphHooks } from '@tunasong/graph-lib/react'
import type { Owned } from '@tunasong/models'
import { isMention, shortUuid } from '@tunasong/models'
import { useSelector } from '@tunasong/redux'
import type { Chat, Comment, Entity, EntityOrElement, Persisted } from '@tunasong/schemas'
import { chatSchema, isPersisted, persistedSchema } from '@tunasong/schemas'
import { useCurrentUser } from '@tunasong/redux'
import { Node } from 'slate'
import invariant from 'tiny-invariant'
import { mergeChatComments } from './merge-chat-comments.js'

export const useChat = <T extends EntityOrElement>({
  element,
  pageSize = 25,
}: {
  element?: T | null
  pageSize?: number
}) => {
  const { userId } = useCurrentUser()

  const presence = useSelector(state => (element?.id ? state.presence.activeClients[element.id] : null))
  const activeUsers = presence ? Object.values(presence) : []
  const { createEntity } = graphHooks.useEntityCreate()
  const updateEntity = graphHooks.useEntityUpdate<Chat>({ debounceDelay: 0 })

  const externalItems = useSelector(state => state.entities.receivedEntities)

  const {
    entities: incrementalEntities = [],
    isFetching,
    isLoading,
    addOptimistic,
    getNext,
  } = graphHooks.useEntityChildrenIncremental<Persisted<Comment>>({
    pageSize,
    includeSys: true,
    parentId: element?.id,
    filter: 'comment',
  })

  // GH #1288: merge with cross-source dedupe by id. The old logic only
  // filtered `externalItems` against `incrementalEntities`, so optimistic +
  // synced collisions within either source produced duplicate React keys.
  const comments = mergeChatComments(incrementalEntities, externalItems, element?.id)

  const create = async (parent: Persisted<Entity>) => {
    /** @todo we use AUTHENTICATED ACL here */
    const chat: Chat & Partial<Owned> = {
      type: 'chat',
      userId,
      parentId: parent.id,
      /** @todo previously for public chats in video rooms */
      // acls: [{ principal: PrincipalType.AUTHENTICATED, permission: 'READ' }],
    }
    const result = createEntity({ entity: chat, parent })
    return chatSchema.extend(persistedSchema.shape).parse(result)
  }

  const addComment = async (comment: Comment) => {
    invariant(userId && element?.id, `Cannot addComment: userId: ${userId}, id: ${element?.id}`)

    /** @todo the ACLs should not really be set here */
    const cc = {
      /** Set the ID to get immediate response in the UI */
      id: shortUuid(),
      // @todo react-compiler makes this crash without the ? operator
      parentId: element?.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...comment,
      // Client-authored comments are chat turns unless the caller says otherwise.
      kind: comment.kind ?? 'chat',
      // acls: [{ principal: PrincipalType.AUTHENTICATED, permission: 'READ' }],
    }
    addOptimistic(cc)
    return createEntity({ entity: cc, parent: element as never }) as Promise<Persisted<Comment>>
  }

  const resolveComment = (c: Comment) => {
    if (!(element && isPersisted(c))) {
      throw new Error(`Unable to update comment when element is null or comment.id is null`)
    }

    return updateEntity(c.id, { resolved: true })
  }

  const sortedComments = comments?.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
  const activeComments = sortedComments?.filter((e: Comment) => Boolean(!e.resolved))
  const resolvedComments = sortedComments?.filter(e => Boolean(e.resolved))

  const isMentioned = activeComments
    ? Boolean(
        activeComments.find(comment => {
          for (const [node] of Node.descendants(comment)) {
            if (isMention(node) && node.userId === userId) {
              return true
            }
          }
          return false
        })
      )
    : false

  return {
    comments,
    getNext,
    isMentioned,
    activeComments,
    resolvedComments,
    activeUsers,
    create,
    addComment,
    resolveComment,
    isLoading,
    isFetching,
  }
}
